import { useState } from "react";

import { STRIPE_STATUS_LABEL, SUBSCRIPTION_STATUS_LABEL } from "../../shared/billing-labels";
import type {
  BillingInvoice,
  BillingOverview,
  BillingSubscription,
} from "../../shared/billing-schemas";
import { Badge } from "../components/ui/badge";
import { cadenceLabel, formatDay, formatUsd } from "./billing-format";
import {
  invoiceActions,
  invoiceBadge,
  PAYMENT_METHOD_LABEL,
  subscriptionBadge,
  subscriptionActions,
  subscriptionDates,
} from "./billing-row-actions";
import { BillingRecordSheet, Fact, type RecordSheetContent } from "./BillingRecordSheet";
import { EmptyCard, PageSection } from "./PageSection";

/**
 * What has been invoiced, and what may be done about it (ADR-0073, ADR-0087): one table, each row
 * opening a side sheet with its details and actions. Past-due rows are visible and nothing more:
 * access is never changed from this screen. The rules live in `billing-row-actions.ts`.
 */

function sourceLabel(record: { source: string; externalReference: string | null }): string {
  if (record.source === "stripe") return "Stripe";
  if (record.source === "manual") return "Recorded by hand";
  return `Another system${record.externalReference ? ` · ${record.externalReference}` : ""}`;
}

function paidLine(invoice: BillingInvoice): string {
  if (invoice.paidAt === null) return "Not yet";
  const method = invoice.paymentMethod
    ? ` by ${PAYMENT_METHOD_LABEL[invoice.paymentMethod].toLowerCase()}`
    : "";
  return `${formatDay(invoice.paidAt)}${method}`;
}

function InvoiceDetails({ invoice }: { invoice: BillingInvoice }) {
  return (
    <dl className="m-0 flex flex-col gap-4">
      <Fact label="Amount">{formatUsd(invoice.amountDueMinor)}</Fact>
      <Fact label="Status">
        <Badge variant={invoiceBadge(invoice.status)}>{STRIPE_STATUS_LABEL[invoice.status]}</Badge>
      </Fact>
      <Fact label="Issued">{formatDay(invoice.issuedAt)}</Fact>
      <Fact label="Due">{formatDay(invoice.dueAt)}</Fact>
      <Fact label="Paid">{paidLine(invoice)}</Fact>
      {invoice.status === "processing" ? (
        <Fact label="Payment settling since">{formatDay(invoice.paymentPendingAt)}</Fact>
      ) : null}
      {invoice.amountRefundedMinor > 0 ? (
        <Fact label="Refunded">{formatUsd(invoice.amountRefundedMinor)}</Fact>
      ) : null}
      {invoice.lastPaymentError ? (
        <Fact label="Last payment attempt">
          <span className="text-destructive">{invoice.lastPaymentError}</span>
        </Fact>
      ) : null}
      <Fact label="Sent through">{sourceLabel(invoice)}</Fact>
    </dl>
  );
}

function invoiceContent(
  invoice: BillingInvoice,
  canManage: boolean,
  canCollect: boolean,
): RecordSheetContent {
  return {
    title: invoice.description,
    kicker: invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : "Invoice",
    details: <InvoiceDetails invoice={invoice} />,
    actions: invoiceActions(invoice, canManage, canCollect),
    stripeUrl: invoice.source === "stripe" ? invoice.hostedInvoiceUrl : null,
  };
}

function SubscriptionDetails({ subscription }: { subscription: BillingSubscription }) {
  return (
    <dl className="m-0 flex flex-col gap-4">
      <Fact label="Price">
        {formatUsd(subscription.amountMinor)} {cadenceLabel(subscription.cadence)}
      </Fact>
      <Fact label="Status">
        <Badge variant={subscriptionBadge(subscription.status)}>
          {SUBSCRIPTION_STATUS_LABEL[subscription.status]}
        </Badge>
      </Fact>
      <Fact label="Dates">{subscriptionDates(subscription)}</Fact>
      <Fact label="Billed through">{sourceLabel(subscription)}</Fact>
    </dl>
  );
}

/** One subscription in a side sheet, opened from the card of the terms it bills. */
export function SubscriptionSheet({
  customerSlug,
  subscription,
  canManage,
  canCollect,
  onChanged,
  onClose,
}: {
  customerSlug: string;
  subscription: BillingSubscription | null;
  canManage: boolean;
  canCollect: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <BillingRecordSheet
      customerSlug={customerSlug}
      recordKey={subscription?.id ?? null}
      content={
        subscription
          ? {
              title: subscription.description,
              kicker: "Subscription",
              details: <SubscriptionDetails subscription={subscription} />,
              actions: subscriptionActions(subscription, canManage, canCollect),
              stripeUrl: null,
            }
          : null
      }
      onDone={onChanged}
      onClose={onClose}
    />
  );
}

function InvoiceTable({
  invoices,
  onOpen,
}: {
  invoices: BillingInvoice[];
  onOpen: (invoice: BillingInvoice) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-xl bg-card shadow-card">
      <table className="w-full min-w-[36rem] border-collapse text-left" aria-label="Invoices">
        <thead>
          <tr className="text-sm text-muted-foreground">
            <th className="px-6 py-3 font-semibold">Invoice</th>
            <th className="px-3 py-3 font-semibold">Issued</th>
            <th className="px-3 py-3 text-right font-semibold">Amount</th>
            <th className="px-6 py-3 font-semibold">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-muted border-t border-muted">
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="hover:bg-muted/60">
              <td className="px-6 py-3.5">
                <button
                  type="button"
                  className="text-left font-semibold text-foreground hover:underline"
                  onClick={() => {
                    onOpen(invoice);
                  }}
                >
                  {invoice.description}
                </button>
                {invoice.invoiceNumber ? (
                  <span className="block text-sm text-muted-foreground">
                    {invoice.invoiceNumber}
                  </span>
                ) : null}
              </td>
              <td className="px-3 py-3.5 text-muted-foreground">{formatDay(invoice.issuedAt)}</td>
              <td className="px-3 py-3.5 text-right font-semibold tabular-nums">
                {formatUsd(invoice.amountDueMinor)}
              </td>
              <td className="px-6 py-3.5">
                <Badge variant={invoiceBadge(invoice.status)}>
                  {STRIPE_STATUS_LABEL[invoice.status]}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Adjustments({ billing }: { billing: BillingOverview }) {
  if (billing.refunds.length === 0 && billing.credits.length === 0) return null;
  return (
    <PageSection title="Refunds and credits" id="billing-adjustments-title" level="h3">
      <ul
        className="divide-y divide-muted rounded-xl bg-card shadow-card"
        aria-label="Refunds and credits"
      >
        {billing.refunds.map((refund) => (
          <li
            key={refund.id}
            className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5"
          >
            <span>
              Refund {formatUsd(refund.amountMinor)} ·{" "}
              {refund.source === "stripe" ? "Stripe" : "by hand"} · {formatDay(refund.createdAt)}
              {refund.reason ? ` · ${refund.reason}` : ""}
            </span>
            <Badge
              variant={
                refund.status === "succeeded"
                  ? "default"
                  : refund.status === "failed"
                    ? "destructive"
                    : "secondary"
              }
            >
              {refund.status}
            </Badge>
          </li>
        ))}
        {billing.credits.map((credit) => (
          <li
            key={credit.id}
            className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5"
          >
            <span>
              Credit {formatUsd(credit.amountMinor)} ·{" "}
              {credit.source === "stripe" ? "Stripe balance" : "recorded by hand"} ·{" "}
              {formatDay(credit.createdAt)}
            </span>
            <Badge variant="secondary">applied</Badge>
          </li>
        ))}
      </ul>
    </PageSection>
  );
}

export function BillingRecords({
  customerSlug,
  billing,
  canManage,
  canCollect,
  onChanged,
}: {
  customerSlug: string;
  billing: BillingOverview;
  canManage: boolean;
  canCollect: boolean;
  onChanged: () => Promise<void>;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  // Read from the overview each render, so the sheet shows the invoice as it is after a change.
  const open = billing.invoices.find((invoice) => invoice.id === openId) ?? null;
  return (
    <>
      <PageSection
        title="Invoices"
        id="billing-invoices-title"
        level="h3"
        description="Everything billed to this customer, through Stripe or recorded from elsewhere. Open one to record a check, void it, or refund it. A past-due invoice never changes what the customer can reach."
      >
        {billing.invoices.length === 0 ? (
          <EmptyCard>No invoices yet. Send one from the terms it bills.</EmptyCard>
        ) : (
          <InvoiceTable
            invoices={billing.invoices}
            onOpen={(invoice) => {
              setOpenId(invoice.id);
            }}
          />
        )}
      </PageSection>
      <BillingRecordSheet
        customerSlug={customerSlug}
        recordKey={openId}
        content={open ? invoiceContent(open, canManage, canCollect) : null}
        onDone={onChanged}
        onClose={() => {
          setOpenId(null);
        }}
      />
      <Adjustments billing={billing} />
    </>
  );
}
